import React, { useState, useEffect } from 'react';
import { TextField, makeStyles } from '@material-ui/core';
import fontFamily from '../../assets/css/fonts';
import color from '../../assets/css/colors';
import QueryParam from '../../services/QueryParamService';
import { getPreviousDate } from '../../services/ProfileService';
import { listBuyingPower } from '../../services/BuyingPowerService';
import Table, { columnType } from '../../components/ClearingTable/Table';
import AutoCompleteAccountName from '../../components/AutoComplete/AutoCompleteAccountName';
import AutoCompleteCorrespondent from '../../components/AutoComplete/AutoCompleteCorrespondent';
import AutoCompleteMasterAccountNo from '../../components/AutoComplete/AutoCompleteMasterAccountNo';
import Button from '../../components/Buttons/Button';
import Text from '../../components/Typography/Text';
import SelectSystemCode from '../../components/Dropdown/SelectSystemCode';
import { notifyInfo, notifyError } from '../../components/Messages/Notification';

const useStyles = makeStyles(() => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    maxWidth: 800,
    width: '100%',
    margin: '0 auto',
    float: 'clear',
  },
  modalHeader: {
    backgroundColor: 'white',
    padding: 25,
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    borderBottom: '1px solid #ccc',
    display: 'flex',
    '& div:nth-child(1)': {
      flex: 1,
    },
    '& div:nth-child(2)': {
      flex: 'none',
      '& .MuiIconButton-root': {
        borderRadius: 5,
        padding: 10,
        '&:hover svg path': {
          fill: color.primary.main,
        },
      },
      '& svg:last-child': {
        width: 15,
        height: 15,
      },
    },
  },
  modalBody: {
    backgroundColor: 'white',
    padding: '0px 25px 25px 25px',
    fontFamily: fontFamily.headers,
    '& p': {
      fontSize: 16,
      lineHeight: '24px',
    },
  },
  modalFooter: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-end',
    backgroundColor: 'white',
    padding: '0px 25px 25px 25px',
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
    position: 'relative',
    '& button': {
      width: 'auto',
    },
  },
  closeButton: {
    '& button': {
      marginTop: '-4px !important',
    },
  },
}));

export default function BuyingPower() {
  const columns = [
    {
      name: 'tradeDate',
      label: 'Trade Date',
      type: columnType.date,
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
    },
    {
      name: 'masterAccountNo',
      label: 'Master Account No',
    },
    {
      name: 'accountName',
      label: 'Account Name',
    },
    {
      name: 'cashBalance',
      label: 'Cash Balance',
      type: columnType.amount,
    },
    {
      name: 'equity',
      label: 'Equity',
      type: columnType.amount,
      options: {
        display: false,
      },
    },
    {
      name: 'exchangeReq',
      label: 'Exchange Req',
      type: columnType.amount,
      options: {
        display: false,
      },
    },
    {
      name: 'houseReq',
      label: 'House Req',
      type: columnType.amount,
      options: {
        display: false,
      },
    },
    {
      name: 'multiplier',
      label: 'Multiplier',
      type: columnType.quantity,
      options: {
        display: false,
      },
    },
    {
      name: 'percentDeduction',
      label: 'Percent Deduction',
      type: columnType.percentage,
    },
    {
      name: 'openingBuyingPower',
      label: 'Opening Buying Power',
      type: columnType.amount,
      options: {
        display: false,
      },
    },
    {
      name: 'deposit',
      label: 'Deposit',
      type: columnType.amount,
      options: {
        display: false,
      },
    },
    {
      name: 'buyingPower',
      label: 'Buying Power',
      type: columnType.amount,
    },
    {
      name: 'buyingPowerUsed',
      label: 'Buying Power Used',
      type: columnType.amount,
    },
    {
      name: 'buyingPowerCall',
      label: 'Buying Power Call',
      type: columnType.amount,
    },
    {
      name: 'buyingPowerPercent',
      label: 'Buying Power Percent',
      type: columnType.percentage,
    },
    {
      name: 'marginType',
      label: 'Margin Type',
    },
    {
      name: 'sma',
      label: 'SMA',
      type: columnType.amount,
    },
    {
      name: 'houseExcess',
      label: 'House Excess',
      type: columnType.amount,
    },
    {
      name: 'exchangeExcess',
      label: 'Exchange Excess',
      type: columnType.amount,
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    serverSide: true,
    selectableRows: 'none',
    responsive: 'standard',
  };

  const [loading, setLoading] = React.useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({});
  const [totalRows, setTotalRows] = useState(0);
  const [searchDataCsv, setSearchDataCsv] = React.useState([]);
  const [previousRows, setPreviousRows] = useState(0);
  const [searchData, setSearchData] = React.useState(
    QueryParam.get({
      fromDate: '',
      toDate: '',
      correspondent: '',
      masterAccountNo: '',
      accountName: '',
      marginType: '',
      hideZero: false,
    })
  );

  useEffect(
    () => {
      async function init() {
        const previousDate = await getPreviousDate();
        var searchDataCopy = { ...searchData };
        searchDataCopy.fromDate = searchDataCopy.fromDate
          ? searchDataCopy.fromDate
          : previousDate;
        searchDataCopy.toDate = searchDataCopy.toDate
          ? searchDataCopy.toDate
          : previousDate;
        setSearchData(searchDataCopy);
      }

      init();
    },
    // eslint-disable-next-line
    []
  );

  useEffect(() => {
    if (rows.length > 0) {
      handleSearch();
    }

    // eslint-disable-next-line
  }, [searchData.hideZero]);

  const handleChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    setSearchData({
      ...searchData,
      [input.name]: input.value,
    });
  };

  const handleSearch = async (paginationChange) => {
    try {
      setLoading(true);
      QueryParam.set(searchData);

      let paginationCopy = {
        ...pagination,
        reload: false,
        pageNo: paginationChange === true ? pagination.pageNo : 0,
        rowsPerPage: pagination.rowsPerPage || 100,
      };

      if (previousRows != 0) {
        if (previousRows < pagination.rowsPerPage * pagination.pageNo) {
          paginationCopy.pageNo = 0;
        }
      }
      if (paginationChange !== true) {
        paginationCopy.filterNames = [];
        paginationCopy.filterValues = [];
      }

      const data = await listBuyingPower(searchData, paginationCopy);

      setSearchDataCsv(searchData);
      setPreviousRows(data.summary.totalRows);
      setTotalRows(data.summary.totalRows);

      paginationCopy.count = data.summary.totalRows;
      setPagination(paginationCopy);
      setRows(data.buyingPowersList);
      if (paginationChange !== true) {
        notifyInfo(data.summary.totalRows + ' search results.');
      }
    } catch (error) {
      console.error(error);
      notifyError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getCsvData = async () => {
    if (totalRows === 0) {
      return [];
    }
    let paginationCopy = {
      ...pagination,
      reload: false,
      pageNo: 0,
      rowsPerPage: totalRows,
    };
    const data = await listBuyingPower(searchDataCsv, paginationCopy);
    const csvData = data.buyingPowersList.map((item) => {
      delete item.accountId;
      delete item.oms;
      delete item.trnsId;
      delete item.client;
      delete item.pagination;
      return item;
    });

    return csvData;
  };

  /*=========================================================================================
    Table component
    ===========================================================================================*/
  return (
    <form className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Buying Power" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading}
              loading={loading}
              type="search"
              label={loading ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
          <div className="grd-cell-none">
            <Button
              label={!searchData.hideZero ? 'Hide Zero Values' : 'Show Zero Values'}
              onClick={() => {
                setSearchData({
                  ...searchData,
                  hideZero: searchData.hideZero ? false : true,
                });
              }}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <TextField
              required
              fullWidth
              name="fromDate"
              label="From Date"
              type="date"
              value={searchData.fromDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: searchData.toDate }}
            />
          </div>
          <div className="grd-cell">
            <TextField
              required
              fullWidth
              name="toDate"
              label="To Date"
              type="date"
              value={searchData.toDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: searchData.fromDate }}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteCorrespondent
              isAllStatus={true}
              name="correspondent"
              value={searchData.correspondent}
              onChange={handleChange}
            ></AutoCompleteCorrespondent>
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountName
              name="accountName"
              value={searchData.accountName}
              correspondent={searchData.correspondent}
              onChange={handleChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteMasterAccountNo
              name="masterAccountNo"
              label="Master Account No"
              value={searchData.masterAccountNo}
              onChange={handleChange}
              isAllStatus={true}
            ></AutoCompleteMasterAccountNo>
          </div>
          <div className="grd-cell">
            <SelectSystemCode
              fullWidth
              name="marginType"
              label="Margin Type"
              type="Margin Type"
              placeholder="Margin Type"
              value={searchData.marginType}
              onChange={handleChange}
              onKeyDown={(e) => (e.keyCode === 13 ? handleSearch() : null)}
            ></SelectSystemCode>
          </div>
        </div>
      </div>
      <div className="mt-20">
        <Table
          title={'Buying Power'}
          data={rows}
          columns={columns}
          options={options}
          getCsvData={getCsvData}
          pagination={pagination}
          setPagination={setPagination}
          onSearch={handleSearch}
        />
      </div>
    </form>
  );
}
